// In-app ABP survey trigger (web) — Refiner. The Refiner web client is loaded via
// a <script> snippet in public/index.html which defines the queue-backed
// `window._refiner(...)` global. Mirrors iOS/Android (survey-*-refiner branches).
//
// The app owns ALL gating: DefaultHome only calls launchSurvey() when the Firebase
// flag is on AND the server's `pendingSurveys` still contains the key (i.e. the
// user is an eligible ABP clinician who has NOT completed it). The audience gate
// lives server-side in get-user, so there is no client-side eligibility here.
//
// Cost-safe: uses `addToResponse` ONLY (never identifyUser/trackEvent), so only
// users who actually respond count toward Refiner MAU billing.
//
// The Refiner project id and form UUID are supplied by the caller from Firebase
// Remote Config, so the survey can be changed per-env WITHOUT a frontend deploy.
// The project id (`refiner_project_id`) is SHARED with iOS/Android, but Refiner
// serves a SEPARATE web-channel form, so web reads its own `refiner_form_uuid_web`
// (the mobile `refiner_form_uuid` renders nothing in the web client). The client
// script is loaded once via public/index.html; we (re)set the project here at
// survey time, mirroring iOS `Refiner.instance.initialize(projectId:)`.

import { API } from "aws-amplify";

/** Shared LOGICAL survey key — identical across iOS/Android/web + the backend. */
const SURVEY_KEY = "abp_end_user_pilot";

type RefinerFn = (command: string, ...args: unknown[]) => void;

function getRefiner(): RefinerFn | undefined {
  return (window as unknown as { _refiner?: RefinerFn })._refiner;
}

// Register the completion listener only once per page load.
let completionListenerRegistered = false;

/**
 * Durably record completion server-side (POST /survey-completion) so a completed
 * user is never shown the survey again — on any platform, across devices. userId
 * is derived from the session on the backend. Best-effort: if the POST fails, the
 * server still reports the survey as pending, so it re-shows next login and
 * completion can be recorded then.
 */
async function recordCompletion(): Promise<void> {
  try {
    await API.post("simpl-api", "survey-completion", {
      body: { surveyKey: SURVEY_KEY, platform: "web", vendor: "refiner" }
    });
  } catch (e) {
    console.warn("survey-completion POST failed", e);
  }
}

/**
 * Attach the IDs as contextual data (hidden fields, no user identification) and
 * show the Refiner form. The queue-backed `_refiner(...)` global (defined in the
 * index.html snippet) accepts calls before the client finishes loading, so no
 * "ready" wait is needed (unlike the Survicate web client).
 *
 * `showForm(uuid, true)` forces display, bypassing Refiner's own targeting/seen
 * rules — the app (not Refiner) owns completion suppression via the server
 * `pendingSurveys` gate in DefaultHome. A dismissed survey simply re-shows next
 * login; only a recorded completion — which drops the key from `pendingSurveys`
 * server-side — stops it.
 */
export function launchSurvey(
  context: Record<string, string | number>,
  ids: { projectId: string; formUuid: string }
): void {
  const { projectId, formUuid } = ids;
  if (!projectId || !formUuid) return;
  const refiner = getRefiner();
  if (!refiner) return;

  if (!completionListenerRegistered) {
    refiner("onComplete", () => {
      void recordCompletion();
    });
    // onDismiss (dismiss) is intentionally not handled: nothing is recorded, so
    // it re-shows next login until completed.
    completionListenerRegistered = true;
  }

  // Set the project from Firebase Remote Config before showing the form; the
  // last setProject wins over the default in the index.html snippet.
  refiner("setProject", projectId);
  refiner("addToResponse", context);
  refiner("showForm", formUuid, true);
}
